import { useState } from "react";
import {
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  List,
  ListItem,
  Skeleton,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { listingsModel } from "models/listings";
import { type APIListingOffer } from "models/types/api-listing";
import { useParams } from "react-router-dom";
import BarChartIcon from "assets/icons/medium/bar-chart.svg?react";
import { SelectInput, type MenuOption } from "components/inputs/select-input";
import { useMatchMenuWidth } from "components/inputs/select-utils";
import { ScreenWrapper } from "components/screen-wrapper";
import { SkeletonGrid } from "components/skeleton-grid";
import { StatsCard } from "components/stats-card";
import { StatsCardSkeleton } from "components/stats-card-skeleton";
import type { Stat } from "components/stats-grid";
import { TotalCountBadge } from "components/total-count-badge";
import { useFormatMoney } from "hooks/use-format-money";
import { SkeletonList } from "modules/vendor-portal/components/skeleton-list";
import { useWhichWordFactory } from "modules/vendor-portal/hooks/use-which-words";
import { OfferListItem } from "modules/vendor-portal/screens/offers/components/offer-list-item";
import {
  getOpenOffers,
  isActiveContract,
  offerFeedbackWithPrice,
} from "modules/vendor-portal/utils/offers";

export enum OffersSortByOption {
  Highest = "highest",
  Lowest = "lowest",
  Recent = "recent",
}

type OffersOptionsGroup = {
  label: { id: string; value: string };
  options: MenuOption[];
};

const SORT_BY_OPTIONS: OffersOptionsGroup[] = [
  {
    label: { id: "sort-by", value: "Sort by" },
    options: [
      { value: OffersSortByOption.Highest, label: "Highest offer" },
      { value: OffersSortByOption.Lowest, label: "Lowest offer" },
      { value: OffersSortByOption.Recent, label: "Most recent" },
    ],
  },
];
const DEFAULT_SORT_BY_OPTION = SORT_BY_OPTIONS[0].options[0].value;

function sortOffers(
  offers: APIListingOffer[],
  sortByValue: string,
): APIListingOffer[] {
  switch (sortByValue) {
    case OffersSortByOption.Highest:
      return offers.sort((a, b) => (b.amount_of ?? 0) - (a.amount_of ?? 0));
    case OffersSortByOption.Lowest:
      return offers.sort((a, b) => (a.amount_of ?? 0) - (b.amount_of ?? 0));
    case OffersSortByOption.Recent:
      return offers.sort(
        (a, b) =>
          new Date(b.date_of_offer).getTime() -
          new Date(a.date_of_offer).getTime(),
      );
    default:
      return offers;
  }
}

function sortActiveContracts(offers: APIListingOffer[]): APIListingOffer[] {
  return offers.sort((a, b) => {
    // Show status unconditional first and then accepted. If both have sane status sort by highest amount
    if (a.status.id === "unconditional" && b.status.id !== "unconditional") {
      return -1;
    }
    if (b.status.id === "unconditional" && a.status.id !== "unconditional") {
      return 1;
    }
    if (a.status.id === "accepted" && b.status.id !== "accepted") {
      return -1;
    }
    if (b.status.id === "accepted" && a.status.id !== "accepted") {
      return 1;
    }
    return (b.amount_of ?? 0) - (a.amount_of ?? 0);
  });
}

export function OffersScreen() {
  const formatMoney = useFormatMoney();
  const { propertyId } = useParams();
  const { data, isLoading } = useQuery(listingsModel.queries.read(propertyId));
  const [sortByValue, setSortByValue] = useState<string>(
    DEFAULT_SORT_BY_OPTION,
  );

  const ww = useWhichWordFactory();

  const matchMenuWidth = useMatchMenuWidth();

  if (!data) return null;

  const offerFeedback = offerFeedbackWithPrice(data.applicant_feedback?.entries);

  const activeContracts = (data.offers ?? []).filter(isActiveContract);

  const stats: Stat[] = [
    {
      id: "highest",
      label: "Highest offer feedback",
      value: formatMoney(
        offerFeedback?.length
          ? offerFeedback?.reduce(
              (max, offer) => Math.max(max, offer.price_indication ?? 0),
              0,
            )
          : 0,
      ),
    },
    {
      id: "average",
      label: "Offer feedback average",
      value: formatMoney(
        offerFeedback?.length
          ? offerFeedback?.reduce(
              (accum, offer) => accum + (offer.price_indication ?? 0),
              0,
            ) / offerFeedback?.length
          : 0,
      ),
    },
    // Only surface "Highest accepted contract" when real accepted/unconditional
    // offers exist. Otherwise it read $0 while offer-status feedback in the list
    // made it look like accepted contracts were present. See CRM-13933.
    ...(activeContracts.length
      ? [
          {
            id: "highest-accepted",
            label: ww("Highest accepted contract"),
            value: formatMoney(
              activeContracts.reduce(
                (max, offer) => Math.max(max, offer.amount_of ?? 0),
                0,
              ),
            ),
          },
        ]
      : []),
    {
      id: "advertised",
      label: "Your advertised price",
      value: data.price_advertise_as,
    },
  ];

  const sortableOffers = getOpenOffers(data.offers).concat(
    offerFeedback?.map((i) => ({
      id: i.id,
      amount_of: i.price_indication!,
      date_of_offer: i.date_of,
      date_accepted: null,
      date_settled: null,
      applicants: i.attendees?.map((i) => i.contact) ?? [],
      date_unconditional: null,
      date_withdrawn: null,
      is_backup_offer: false,
      is_primary_backup_offer: false,
      amount_of_includes_tax: false,
      status: { id: "offer", label: "Offer feedback" },
      agent: i.agent,
    })) ?? [],
  );

  return (
    <ScreenWrapper>
      <Heading size={{ base: "2xl", md: "lg" }} fontWeight={700} as={"h1"}>
        {ww("Offers")}
      </Heading>
      <SkeletonGrid
        isLoaded={!isLoading}
        gridSize={stats.length}
        skeleton={<StatsCardSkeleton />}
      >
        {data?.offers?.length ? (
          <Grid
            templateColumns={{
              base: "repeat(2, 1fr)",
              lg: `repeat(${stats.length}, 1fr)`,
            }}
            templateRows={{ base: "repeat(2, 1fr)", lg: "repeat(1, 1fr)" }}
            gap={6}
          >
            {stats.map((stat) => (
              <GridItem key={stat.id} w={"100%"}>
                <StatsCard stat={stat} icon={BarChartIcon} />
              </GridItem>
            ))}
          </Grid>
        ) : null}
      </SkeletonGrid>

      {!!activeContracts.length && (
        <>
          <HStack gap={3}>
            <Heading fontSize={"xl"} as={"h2"}>
              Active {ww("contracts")}
            </Heading>
            <TotalCountBadge count={activeContracts?.length} />
          </HStack>

          <SkeletonList
            isLoaded={!isLoading}
            listSize={4}
            skeleton={<Skeleton h={"119px"} />}
          >
            <List spacing={"12px"}>
              {sortActiveContracts(activeContracts).map((offer) => (
                <ListItem key={offer.id}>
                  <OfferListItem offer={offer} />
                </ListItem>
              ))}
            </List>
          </SkeletonList>
        </>
      )}

      {!!sortableOffers.length && (
        <>
          <Flex
            direction={{ base: "column", md: "row" }}
            justify={"space-between"}
            gap={4}
          >
            <HStack gap={3}>
              <Heading fontSize={"xl"} as={"h2"}>
                Offers
              </Heading>
              <TotalCountBadge count={sortableOffers.length} />
            </HStack>
            <SelectInput
              optionGroups={SORT_BY_OPTIONS}
              value={sortByValue}
              onChange={(value) => setSortByValue(value)}
              menuProps={{ matchWidth: matchMenuWidth }}
            />
          </Flex>

          <SkeletonList
            isLoaded={!isLoading}
            listSize={4}
            skeleton={<Skeleton h={"119px"} />}
          >
            <List spacing={"12px"}>
              {sortOffers(sortableOffers, sortByValue)?.map((offer) => (
                <ListItem key={offer.id}>
                  <OfferListItem offer={offer} />
                </ListItem>
              ))}
            </List>
          </SkeletonList>
        </>
      )}
    </ScreenWrapper>
  );
}
